import { client as fieldRepClient } from "@labeling-tools/api/field-rep/generated/client.gen";
import {
  appsProductTrackLabelingLabelerApiDisputeTask,
  appsProductTrackLabelingLabelerApiListProductSets,
  appsProductTrackLabelingLabelerApiListTasks,
  appsProductTrackLabelingLabelerApiNextTask,
  appsProductTrackLabelingLabelerApiOpenTask,
  appsProductTrackLabelingLabelerApiProductImages,
  appsProductTrackLabelingLabelerApiReleaseTask,
  appsProductTrackLabelingLabelerApiSearchProducts,
  appsProductTrackLabelingLabelerApiSubmitTask,
  appsUsersApiGetCurrentUser,
} from "@labeling-tools/api/field-rep/generated/sdk.gen";
import type {
  GetCurrentUserResponse,
  ProductImageItem,
  ProductListItem,
  ProductSetToLabel,
  SubmitTrackResultResponse,
  TaskListResponse,
  TrackResult,
  TrackTaskDetail,
} from "@labeling-tools/api/field-rep/generated/types.gen";

fieldRepClient.setConfig({ baseUrl: import.meta.env.VITE_FIELD_REP_BASE_URL });

/** An error from field-rep's labeler API. status is null when no response came back. */
class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiResult<T> = {
  data?: T;
  error?: unknown;
  response?: Response;
};

// The backend explains its errors as `{detail: "<message>"}`, e.g. "Task … is held by another
// reviewer". Anything else (validation, network failures) gets a generic message.
const errorMessage = (error: unknown, status: number | null): string => {
  if (
    error &&
    typeof error === "object" &&
    "detail" in error &&
    typeof error.detail === "string"
  ) {
    return error.detail;
  }

  if (status === null) {
    return "Couldn't reach the server. Please try again.";
  }

  return `The server returned an error (${status}). Please try again.`;
};

const toApiError = (
  error: unknown,
  response: Response | undefined,
): ApiError => {
  const status = response?.status ?? null;
  return new ApiError(errorMessage(error, status), status);
};

const unwrap = <T>({ data, error, response }: ApiResult<T>): T => {
  if (error !== undefined || !response?.ok) {
    throw toApiError(error, response);
  }
  return data as T;
};

/**
 * The task can't be worked on any more, e.g. someone else holds or finished it (409),
 * or it's gone or no longer accessible (404). Retrying won't help, so the labeler
 * should move on.
 */
export const isTaskUnavailable = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 409 || error.status === 404);

/** The user logged in to Firebase, but field-rep has no account for them (401). */
export const isAccountMissing = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

export const taskQueryKey = (taskId: string) => ["task", taskId] as const;

/** Fetches the next task in the product set. null when nothing is left to label. */
export const fetchNextTask = async (
  productSetId: string,
): Promise<TrackTaskDetail | null> => {
  const result = await appsProductTrackLabelingLabelerApiNextTask({
    query: { product_set_id: productSetId },
  });

  const task = unwrap<unknown>(result);
  return result.response?.status === 204 ? null : (task as TrackTaskDetail);
};

export const openTask = async (taskId: string): Promise<TrackTaskDetail> =>
  unwrap(
    await appsProductTrackLabelingLabelerApiOpenTask({
      path: { labeling_task_id: taskId },
    }),
  );

export const submitTask = async (
  taskId: string,
  result: TrackResult,
): Promise<SubmitTrackResultResponse> =>
  unwrap(
    await appsProductTrackLabelingLabelerApiSubmitTask({
      path: { labeling_task_id: taskId },
      body: { result },
    }),
  );

export const disputeTask = async (
  taskId: string,
  note: string,
): Promise<void> => {
  unwrap(
    await appsProductTrackLabelingLabelerApiDisputeTask({
      path: { labeling_task_id: taskId },
      body: { note },
    }),
  );
};

export const releaseTask = async (taskId: string): Promise<void> => {
  unwrap(
    await appsProductTrackLabelingLabelerApiReleaseTask({
      path: { labeling_task_id: taskId },
    }),
  );
};

/** Searches the task's product set, for picking a product the recognizer didn't propose. */
export const searchTaskProducts = async (
  taskId: string,
  search: string,
): Promise<ProductListItem[]> =>
  unwrap(
    await appsProductTrackLabelingLabelerApiSearchProducts({
      path: { labeling_task_id: taskId },
      query: { search, page_size: 100 },
    }),
  ).items;

/** Lists a product's catalog images; the product must be proposed for the task or in its set. */
export const fetchTaskProductImages = async (
  taskId: string,
  productId: string,
): Promise<ProductImageItem[]> =>
  unwrap(
    await appsProductTrackLabelingLabelerApiProductImages({
      path: { labeling_task_id: taskId, product_id: productId },
    }),
  ).images;

export const fetchCurrentUser = async (): Promise<GetCurrentUserResponse> =>
  unwrap(await appsUsersApiGetCurrentUser());

/**
 * Lists unfinished tasks. Labelers only see pending tasks. Staff also see in-review tasks,
 * to release them.
 */
export const listTasks = async (
  page: number,
  pageSize: number,
  productSetId: string | null,
  { isStaff }: { isStaff: boolean },
): Promise<TaskListResponse> =>
  unwrap(
    await appsProductTrackLabelingLabelerApiListTasks({
      query: {
        page,
        page_size: pageSize,
        status: isStaff ? ["PENDING", "IN_REVIEW"] : ["PENDING"],
        product_set_id: productSetId,
      },
    }),
  );

/** The product sets with tasks waiting to be labeled. */
export const listProductSets = async (): Promise<ProductSetToLabel[]> =>
  unwrap(await appsProductTrackLabelingLabelerApiListProductSets()).items;
